import { computed, reactive, ref } from "vue";
import { defineStore } from "pinia";
import {
  type TokenResponse,
  type UserDto,
  TokenService,
  UserService,
  OpenAPI,
} from "@/client";
import type { Jwt } from "@/models/jwt";
import { event } from 'vue-gtag'
import { jwtDecode } from "jwt-decode";

export const useUserStore = defineStore("user", () => {
  const users = ref([] as UserDto[]);
  const tenant = ref("");
  const user = ref(JSON.parse(localStorage.getItem("user") || "null"));
  const auth = ref(JSON.parse(localStorage.getItem("auth") || "null"));
  const decodedAuth = ref(
    auth.value != null
      ? (jwtDecode(auth.value?.token) as Jwt)
      : JSON.parse("null"),
  );
  const locationId = ref("");

  if (auth.value != null) {
    OpenAPI.TOKEN = auth.value!.token!;
    decodedAuth.value = jwtDecode(auth.value!.token) as Jwt;
    locationId.value = decodedAuth.value.lid;
    tenant.value = decodedAuth.value.ten;
    if (decodedAuth.value.exp < Date.now() / 1000) {
      logout();
    }
  }

  function logout() {
    event('logout')
    auth.value = null;
    user.value = null;
    OpenAPI.TOKEN = undefined;
    localStorage.clear();
    window.location.reload();
  }

  const isAuthenticated = computed(() => {
    if (auth.value != null) {
      if (decodedAuth.value.exp < Date.now() / 1000) {
        logout();
        return false;
      }
      return true;
    }
    return false;
  });

  async function changePassword(oldPassword: string, password: string) {
    return await UserService.postApiUserChangePassword({ requestBody: { password: oldPassword, newPassword: password } })
      .then((data) => Promise.resolve(data))
      .catch((error: any) => Promise.reject(error));
  }

  async function login(email: string, password: string) {
    return await TokenService.postApiToken({ requestBody: { email, password } })
      .then(async (data) => {
        event('login', { method: 'Normal' });
        await setLogin(data!);
        return Promise.resolve(data);
      })
      .catch((error: any) => Promise.reject(error));
  }

  async function setLogin(response: TokenResponse) {
    auth.value = response;
    decodedAuth.value = reactive(jwtDecode(auth.value.token) as Jwt);
    tenant.value = decodedAuth.value.ten;
    locationId.value = decodedAuth.value.lid;
    localStorage.setItem("auth", JSON.stringify(response));
    OpenAPI.TOKEN = response.token!;
    await getCurrentUser();
  }

  async function getCurrentUser() {
    decodedAuth.value = jwtDecode(auth.value!.token) as Jwt;
    locationId.value = decodedAuth.value.lid;
    user.value = (await getUser(decodedAuth.value.uid)) as UserDto;
    localStorage.setItem("user", JSON.stringify(user.value));
    return user.value;
  }

  async function getUser(id: string) {
    return await UserService.getApiUserById({ id })
      .then((data) => Promise.resolve(data))
      .catch((error: any) => Promise.reject(error));
  }

  async function getUsers() {
    return await UserService.getApiUser()
      .then((data) => {
        users.value = data!;
        return Promise.resolve(data);
      })
      .catch((error: any) => Promise.reject(error));
  }

  async function forgotPassword(email: string) {
    return await UserService.postApiUserForgotPassword({ requestBody: { email } })
      .then((data) => Promise.resolve(data))
      .catch((error: any) => Promise.reject(error));
  }

  async function setLocation(newLocationId: string) {
    return await TokenService.postApiTokenSetLocation({ requestBody: { id: newLocationId } })
      .then((data) => {
        auth.value = data;
        decodedAuth.value = jwtDecode(auth.value.token) as Jwt;
        localStorage.setItem("auth", JSON.stringify(data));
        OpenAPI.TOKEN = data.token;
        getCurrentUser();
        return Promise.resolve(data);
      })
      .catch((error: any) => Promise.reject(error));
  }

  async function setTenant(tenantId: string, reload: boolean = true) {
    return await TokenService.postApiTokenSetTenant({ requestBody: { id: tenantId } })
      .then((data) => {
        auth.value = data;
        decodedAuth.value = jwtDecode(auth.value.token) as Jwt;
        tenant.value = decodedAuth.value.ten;
        localStorage.setItem("auth", JSON.stringify(data));
        OpenAPI.TOKEN = data.token;
        getCurrentUser();
        if (reload === true) window.location.reload();
        return Promise.resolve(data);
      })
      .catch((error: any) => Promise.reject(error));
  }

  async function getRoles() {
    return await UserService.getApiUserRoles()
      .then((data) => Promise.resolve(data))
      .catch((error: any) => Promise.reject(error));
  }

  async function add(newuser: UserDto, password: string) {
    return await UserService.putApiUserSimple({
      requestBody: {
        email: newuser.email,
        firstName: newuser.firstName,
        lastName: newuser.lastName,
        phoneNumber: newuser.phoneNumber,
        role: newuser.roles![0],
        locationIds: newuser.locations != undefined ? newuser.locations.map(x => x.locationId!) : [],
        password: password,
        username: newuser.email,
        tenantId: decodedAuth.value.tid,
      }
    })
      .then((data) => Promise.resolve(data))
      .catch((error: any) => Promise.reject(error));
  }

  async function update(newuser: UserDto) {
    return await UserService.postApiUser({
      requestBody: {
        id: newuser.id,
        email: newuser.email,
        firstName: newuser.firstName,
        lastName: newuser.lastName,
        phoneNumber: newuser.phoneNumber,
        roles: newuser.roles,
        locations: newuser.locations != undefined ? newuser.locations : [],
        settings: newuser.settings
      }
    })
      .then((data) => Promise.resolve(data))
      .catch((error: any) => Promise.reject(error));
  }

  async function disable(id: string) {
    return await UserService.deleteApiUser({ requestBody: { id } })
      .then((data) => Promise.resolve(data))
      .catch((error: any) => Promise.reject(error));
  }

  async function exists(email: string) {
    return await UserService.getApiUserExistsByEmail({ email })
      .then((data) => Promise.resolve(data))
      .catch((error: any) => Promise.reject(error));
  }

  async function resetPassword(email: string, token: string, password: string) {
    return await UserService.postApiUserResetPassword({ requestBody: { email, token, password } })
      .then((data) => Promise.resolve(data))
      .catch((error: any) => Promise.reject(error));
  }

  async function setPassword(id: string, password: string) {
    return await UserService.postApiUserPassword({ requestBody: { id, password } })
      .then((data) => Promise.resolve(data))
      .catch((error: any) => Promise.reject(error));
  }

  async function updateLoginOption(option: number) {
    return await UserService.postApiUserOption({ requestBody: { id: option } })
      .then((data) => {
        getCurrentUser();
        return Promise.resolve(data);
      })
      .catch((error: any) => Promise.reject(error));
  }

  return {
    changePassword,
    login,
    forgotPassword,
    getCurrentUser,
    logout,
    setLocation,
    getUsers,
    getUser,
    getRoles,
    add,
    update,
    exists,
    disable,
    resetPassword,
    setTenant,
    setPassword,
    updateLoginOption,
    setLogin,

    isAuthenticated,
    user,
    users,
    auth,
    decodedAuth,
    locationId,
    tenant
  };
});
